import React, { useState } from 'react';
import {
  X,
  PackagePlus,
  PackageMinus,
  Save,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Package,
} from 'lucide-react';
import stockService from '@/services/stockService';
/**
 * Stock Update Modal Component
 *
 * Modal for updating stock levels with:
 * - Add/Deduct toggle
 * - Quantity input
 * - Reason selection (for deductions)
 * - Notes field
 * - Real-time preview of new stock level
 */

// Adjustment reasons
const ADJUSTMENT_REASONS = [
  { value: 'purchase', label: 'Purchase / Restock', type: 'add' },
  { value: 'return', label: 'Supplier Return (received back)', type: 'add' },
  { value: 'found', label: 'Stock Found / Counting Correction', type: 'add' },
  { value: 'wastage', label: 'Wastage / Spoiled', type: 'deduct' },
  { value: 'adjustment', label: 'adjustment', type: 'deduct' },
  // { value: 'theft', label: 'Theft / Lost', type: 'deduct' },
  // { value: 'damaged', label: 'Damaged', type: 'deduct' },
  // { value: 'expired', label: 'Expired', type: 'deduct' },
  // { value: 'correction', label: 'Counting Correction', type: 'deduct' },
  // { value: 'consumed', label: 'Manual Consumption', type: 'deduct' },
  { value: 'other', label: 'Other', type: 'add' },
];

export default function StockUpdateModal({ item, uomSymbol, onSave, onClose }) {
  // Form state
  const [updateType, setUpdateType] = useState('add'); // 'add' or 'deduct'
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [unitCost, setUnitCost] = useState(item.unitCost || '');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Calculate new stock
  const parsedQuantity = parseFloat(quantity) || 0;
  const newStock = updateType === 'add'
    ? item.currentStock + parsedQuantity
    : Math.max(0, item.currentStock - parsedQuantity);

  // Filter reasons based on update type
  const filteredReasons = ADJUSTMENT_REASONS.filter(
    r => r.type === 'both' || r.type === updateType
  );

  // Get stock status
  const getStockStatus = (stock) => {
    if (stock <= 0) return 'out';
    if (stock <= item.minimumStock) return 'low';
    return 'healthy';
  };

  const currentStatus = getStockStatus(item.currentStock);
  const newStatus = getStockStatus(newStock);

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!quantity || parsedQuantity <= 0) {
      newErrors.quantity = 'Please enter a valid quantity';
    }

    if (updateType === 'deduct' && parsedQuantity > item.currentStock) {
      newErrors.quantity = 'Cannot deduct more than current stock';
    }

    if (!reason) {
      newErrors.reason = 'Please select a reason';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
const handleSave = async () => {
  if (!validate()) return;

  setIsSaving(true);

  try {
    if (updateType === "add") {
      await stockService.addStock({
        idInventoryItem: item.id,
        quantity: parsedQuantity,
        unitCost: parseFloat(unitCost) || item.unitCost,
        supplierName: supplierName || null,
        notes: notes || null,
        reason: reason || null
      });
    } else {
      await stockService.deductStock({
        idInventoryItem: item.id,
        type: reason, 
        quantity: parsedQuantity,
        reason: reason,
      });
    }
    onSave();
    onClose();
  } 
  catch (error) {
    console.error("Stock update failed:", error);
    alert("Stock update failed. Check console for details.");
  } 
  finally {
    setIsSaving(false);
  }
};


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              updateType === 'add' ? 'bg-green-500' : 'bg-amber-500'
            }`}>
              {updateType === 'add' ? (
                <PackagePlus size={20} className="text-white" />
              ) : (
                <PackageMinus size={20} className="text-white" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Update Stock</h2>
              <p className="text-xs text-slate-500">{item.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Current Stock Display */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Current Stock</p>
                <p className={`text-2xl font-bold ${
                  currentStatus === 'out' ? 'text-red-600' :
                  currentStatus === 'low' ? 'text-amber-600' : 'text-slate-900'
                }`}>
                  {item.currentStock.toFixed(2)} <span className="text-sm font-normal text-slate-500">{uomSymbol}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                {currentStatus === 'out' && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                    Out of Stock
                  </span>
                )}
                {currentStatus === 'low' && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Low Stock
                  </span>
                )}
                {currentStatus === 'healthy' && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    Healthy
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Update Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Update Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setUpdateType('add'); setReason(''); }}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  updateType === 'add'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                <TrendingUp size={18} />
                <span className="font-medium">Add Stock</span>
              </button>
              <button
                type="button"
                onClick={() => { setUpdateType('deduct'); setReason(''); }}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  updateType === 'deduct'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                <TrendingDown size={18} />
                <span className="font-medium">Deduct Stock</span>
              </button>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Quantity <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  if (errors.quantity) setErrors(prev => ({ ...prev, quantity: null }));
                }}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.quantity ? 'border-red-300 bg-red-50' : 'border-slate-200'
                }`}
                autoFocus
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                {uomSymbol}
              </span>
            </div>
            {errors.quantity && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.quantity}
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Reason <span className="text-red-500">*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errors.reason) setErrors(prev => ({ ...prev, reason: null }));
              }}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white ${
                errors.reason ? 'border-red-300 bg-red-50' : 'border-slate-200'
              }`}
            >
              <option value="">Select reason...</option>
              {filteredReasons.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {errors.reason && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <AlertCircle size={12} />
                {errors.reason}
              </p>
            )}
          </div>

          {/* Supplier & Cost (for Add/Purchase) */}
          {updateType === 'add' && reason === 'purchase' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Supplier Name
                </label>
                <input
                  type="text"
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Unit Cost
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">₹</span>
                  <input
                    type="number"
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    placeholder={item.unitCost?.toFixed(2)}
                    step="0.01"
                    min="0"
                    className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Notes <span className="text-xs text-slate-400">(Optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes..."
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
          </div>

          {/* New Stock Preview */}
          {parsedQuantity > 0 && (
            <div className={`rounded-lg p-4 border-2 ${
              updateType === 'add' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">New Stock Level</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-slate-500">{item.currentStock.toFixed(2)}</span>
                    <span className={updateType === 'add' ? 'text-green-600' : 'text-amber-600'}>
                      {updateType === 'add' ? '+' : '-'} {parsedQuantity.toFixed(2)}
                    </span>
                    <span className="text-slate-500">=</span>
                    <span className={`text-xl font-bold ${
                      newStatus === 'out' ? 'text-red-600' :
                      newStatus === 'low' ? 'text-amber-600' : 'text-green-600'
                    }`}>
                      {newStock.toFixed(2)} {uomSymbol}
                    </span>
                  </div>
                </div>
                {newStatus !== currentStatus && (
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    newStatus === 'healthy' ? 'bg-green-100 text-green-700' :
                    newStatus === 'low' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {newStatus === 'healthy' ? '✓ Healthy' :
                     newStatus === 'low' ? '⚠ Low Stock' : '✕ Out of Stock'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !quantity}
            className={`flex items-center gap-2 px-5 py-2 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
              updateType === 'add'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save size={16} />
                {updateType === 'add' ? 'Add Stock' : 'Deduct Stock'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
